import { REPORT_TYPES } from '@/@admin/types/constants';
import { ReportType } from '@/@admin/types/entities';
import { STUDIO_AI_NAME } from '@/@studio-ai/constants';
import { tracking } from '@/lib/features/tracking';
import ExcelJS from 'exceljs';

/**
 * Get the default sort key for a report type
 */
export const getDefaultSortKey = (reportType: ReportType): string => {
    const defaultSorts: Record<ReportType, string> = {
        USERS: 'org_user_id',
        CLIENTS: 'org_id',
        LICENSES: 'org_license_id',
        DASHBOARDS: 'template_id',
        STUDIO_AI_CHAT_HISTORY: 'lastChangedTimestamp',
    };

    return defaultSorts[reportType] || 'id';
};

/**
 * Format sort value for API consumption
 */
export const formatSortValue = (sortKey: string, direction: 'ASC' | 'DESC' = 'DESC'): string => {
    return `${sortKey} ${direction}`;
};

/**
 * Parse sort value from API response
 */
export const parseSortValue = (sortValue: string): { key: string; direction: 'ASC' | 'DESC' } => {
    const [rawKey, rawDirection] = sortValue.trim().split(/\s+/);
    const upperDirection = rawDirection?.toUpperCase();
    const normalizedDirection =
        upperDirection === 'ASC' || upperDirection === 'DESC' ? upperDirection : 'DESC';

    return {
        key: rawKey || 'id',
        direction: normalizedDirection,
    };
};

/**
 * Validate report type
 */
export const isValidReportType = (reportType: string): reportType is ReportType => {
    return Object.keys(REPORT_TYPES).includes(reportType as ReportType);
};

/**
 * Get report display name
 */
export const getReportDisplayName = (reportType: ReportType): string => {
    const displayNames: Record<ReportType, string> = {
        USERS: 'Users',
        CLIENTS: 'Clients',
        LICENSES: 'Licenses',
        DASHBOARDS: 'Dashboards',
        STUDIO_AI_CHAT_HISTORY: `${STUDIO_AI_NAME} Chat History`,
    };

    return displayNames[reportType];
};

/**
 * Check if a report type supports updates
 */
export const supportsUpdates = (reportType: ReportType): boolean => {
    const updateableReports: ReportType[] = ['USERS', 'CLIENTS', 'DASHBOARDS'];

    return updateableReports.includes(reportType);
};

/**
 * Sanitize a cell value for CSV export
 * Escapes quotes and mitigates formula injection
 */
const sanitizeCSVCell = (val: unknown): string => {
    const s = String(val ?? '');
    // Escape quotes for CSV and mitigate formula injection
    const escaped = s.replace(/"/g, '""');
    return /^[=+\-@]/.test(escaped) ? `'${escaped}` : escaped;
};

/**
 * Export marked messages to CSV
 */
export const exportMarkedMessagesToCSV = (
    messages: Array<{
        id: string | number;
        status: string;
        userEmail?: string | null;
        userFirstName?: string | null;
        userLastName?: string | null;
        conversationTitle?: string | null;
        userMessage?: string | null;
        aiResponse?: string | null;
        notes?: string | null;
        createdBy: string;
        createdTimestamp: string | Date;
    }>,
    statusLabels: Record<string, string>,
    formatDateTime: (date: string | Date) => string,
    filename?: string,
) => {
    if (!messages.length) return;

    // Create CSV content
    const headers = [
        'ID',
        'Status',
        'User Email',
        'User Name',
        'Conversation Title',
        'User Message',
        'AI Response',
        'Notes',
        'Created By',
        'Created Date',
    ];

    const rows = messages.map((marker) => [
        marker.id,
        statusLabels[marker.status],
        sanitizeCSVCell(marker.userEmail),
        sanitizeCSVCell(`${marker.userFirstName || ''} ${marker.userLastName || ''}`.trim()),
        sanitizeCSVCell(marker.conversationTitle),
        sanitizeCSVCell(marker.userMessage),
        sanitizeCSVCell(marker.aiResponse),
        sanitizeCSVCell(marker.notes),
        sanitizeCSVCell(marker.createdBy),
        sanitizeCSVCell(formatDateTime(marker.createdTimestamp)),
    ]);

    const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:]/g, '-');
    const defaultFilename =
        messages.length === 1
            ? `marked_message_${messages[0].id}_${timestamp}.csv`
            : `marked_messages_${timestamp}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename || defaultFilename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Generic function to export data to XLSX format using ExcelJS
 * @param data - Array of objects to export or object with named sheets
 * @param filename - Name of the file to download (without extension)
 * @param sheetName - Optional name of the sheet (default: 'Sheet1')
 */
export const exportToXLSX = async (
    data: Record<string, unknown>[] | Record<string, Record<string, unknown>[]>,
    filename: string,
    sheetName?: string,
) => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
        console.warn('No data to export');
        return;
    }

    try {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'MFour Studio';
        workbook.created = new Date();

        // Handle multiple sheets if data is an object
        if (!Array.isArray(data)) {
            Object.entries(data).forEach(([name, sheetData]) => {
                if (sheetData && sheetData.length > 0) {
                    addWorksheetWithData(workbook, name, sheetData);
                }
            });
        } else {
            // Single sheet
            addWorksheetWithData(workbook, sheetName || 'Sheet1', data);
        }

        // Generate timestamp for unique filename
        const timestamp = new Date().toISOString().replace(/[:]/g, '-').split('.')[0];
        const finalFilename = `${filename}_${timestamp}.xlsx`;

        // Write and download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = finalFilename;
        link.click();
        URL.revokeObjectURL(url);
    } catch (error) {
        tracking.errorWithSource(error as Error, 'exportToXLSX', {
            error,
            data,
            filename,
            sheetName,
        });
        throw error;
    }
};

/**
 * Add a worksheet with data and auto-fit columns
 */
const addWorksheetWithData = (
    workbook: ExcelJS.Workbook,
    sheetName: string,
    data: Record<string, unknown>[],
) => {
    if (!data || data.length === 0) return;

    const worksheet = workbook.addWorksheet(sheetName);

    // Get column headers from first row
    const headers = Object.keys(data[0]);

    // Add header row with styling
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
    };

    // Add data rows
    data.forEach((row) => {
        const values = headers.map((header) => {
            const value = row[header];
            // Handle various data types
            if (value === null || value === undefined) return '';
            if (value instanceof Date) return value;
            return value;
        });
        worksheet.addRow(values);
    });

    // Auto-fit columns
    worksheet.columns.forEach((column, index) => {
        if (!column) return;
        const header = headers[index];
        let maxLength = header ? header.length : 10;

        // Check data rows for max length
        data.forEach((row) => {
            const value = row[header];
            const cellLength = String(value ?? '').length;
            if (cellLength > maxLength) {
                maxLength = cellLength;
            }
        });

        // Set column width with padding, capped at 50
        column.width = Math.min(maxLength + 2, 50);
    });

    // Freeze header row
    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
};

/**
 * Format date to readable string for exports
 */
export const formatDateForExport = (date: Date | string | null): string => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    });
};
